import { createLogger } from "./logger";
import type { EventLog, LogEntry } from "./eventLog";

const log = createLogger("persistence");
const STORE = "entries";
const RECORD_KEY = "session";

/**
 * Mirrors the event log to IndexedDB so a workspace iframe reload does not destroy a
 * session in progress. Every operation degrades to a no-op rather than throwing, because
 * losing the mirror must never take down the kit.
 */
export class IndexedDbMirror {
  /**
   * @param dbName - IndexedDB database name.
   * @param factory - IndexedDB factory to use, or `null` when IndexedDB is unavailable
   *   (the unavailable sentinel — see amendment A5). Defaults to `globalThis.indexedDB`.
   */
  constructor(
    private readonly dbName = "cawd",
    private readonly factory: IDBFactory | null = globalThis.indexedDB ?? null,
  ) {}

  /** Reads the mirrored session, or an empty array if absent or unavailable. */
  async load(): Promise<LogEntry[]> {
    return this.withStore("readonly", (store) => store.get(RECORD_KEY))
      .then((v) => (Array.isArray(v) ? (v as LogEntry[]) : []))
      .catch((e) => {
        log.warn("mirror load failed; starting with an empty session", e);
        return [];
      });
  }

  /** Overwrites the mirrored session. */
  async save(entries: readonly LogEntry[]): Promise<void> {
    await this.withStore("readwrite", (store) =>
      store.put([...entries], RECORD_KEY),
    ).catch((e) => {
      log.warn("mirror save failed; continuing in memory only", e);
    });
  }

  /** Empties the mirror. */
  async clear(): Promise<void> {
    await this.withStore("readwrite", (store) =>
      store.delete(RECORD_KEY),
    ).catch((e) => {
      log.warn("mirror clear failed", e);
    });
  }

  /**
   * Opens the database, runs `op` inside a transaction of `mode`, and resolves with the
   * request's result once the TRANSACTION completes (not merely once the request succeeds,
   * since a request can succeed and the transaction still abort afterward). Any synchronous
   * throw from `db.transaction` or `op` itself is caught and turned into a rejection so the
   * returned promise always settles — see amendment A5.
   */
  private withStore<T>(
    mode: IDBTransactionMode,
    op: (s: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T | undefined> {
    if (!this.factory) return Promise.resolve(undefined);
    const factory = this.factory;
    return new Promise((resolve, reject) => {
      const open = factory.open(this.dbName, 1);
      open.onupgradeneeded = () => {
        open.result.createObjectStore(STORE);
      };
      open.onerror = () => reject(open.error);
      open.onsuccess = () => {
        const db = open.result;
        let result: T | undefined;
        try {
          const tx = db.transaction(STORE, mode);
          const req = op(tx.objectStore(STORE));
          req.onsuccess = () => {
            result = req.result;
          };
          tx.oncomplete = () => {
            resolve(result);
            db.close();
          };
          tx.onabort = () => {
            reject(tx.error);
            db.close();
          };
          tx.onerror = () => {
            reject(tx.error);
            db.close();
          };
        } catch (e) {
          db.close();
          reject(e);
        }
      };
    });
  }
}

/**
 * Debounced write-through from an EventLog to a mirror. Flushes the latest snapshot on
 * detach and on `pagehide` (e.g. a workspace iframe reload) so at most the events since the
 * last successful save are ever lost, never up to a full debounce window's worth.
 *
 * @returns A detach function that flushes any pending write, then unsubscribes.
 */
export function attachMirror(
  source: EventLog,
  mirror: IndexedDbMirror,
  debounceMs = 1000,
): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let latest: readonly LogEntry[] = source.entries();
  const flush = (): void => {
    void mirror.save(latest);
  };
  const off = source.subscribe((entries) => {
    latest = entries;
    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, debounceMs);
  });
  const onHide = (): void => {
    if (timer) clearTimeout(timer);
    flush();
  };
  globalThis.addEventListener?.("pagehide", onHide);
  return () => {
    if (timer) clearTimeout(timer);
    globalThis.removeEventListener?.("pagehide", onHide);
    flush();
    off();
  };
}
